import { runtimeConfig } from "@/shared/config/runtime";

const NCALAYER_WS = "wss://127.0.0.1:13579";
const NCALAYER_TIMEOUT_MS = 120_000;
const DEMO_SIGNATURE = "";

export interface NCALayerResult {
  signature: string;
}

export class NCALayerError extends Error {
  code?: string;

  constructor(messageKey: string, code?: string) {
    super(messageKey);
    this.name = "NCALayerError";
    this.code = code;
  }
}

export const NCALAYER_CANCELLED_CODE = "500";

export function signWithNCALayer(dataBase64: string, signal?: AbortSignal): Promise<NCALayerResult> {
  if (runtimeConfig.isDemo) {
    return Promise.resolve({ signature: DEMO_SIGNATURE });
  }

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new NCALayerError("ncalayer.errors.cancelled", NCALAYER_CANCELLED_CODE));
      return;
    }

    let ws: WebSocket;

    try {
      ws = new WebSocket(NCALAYER_WS);
    } catch {
      reject(new NCALayerError("ncalayer.errors.cannotConnect"));
      return;
    }

    let handshakeConsumed = false;
    let canceled = false;
    let settled = false;

    const timeout = setTimeout(() => {
      settle();
      ws.close();
      reject(new NCALayerError("ncalayer.errors.timeout"));
    }, NCALAYER_TIMEOUT_MS);

    function settle(): boolean {
      if (settled) return false;
      settled = true;
      clearTimeout(timeout);
      signal?.removeEventListener("abort", handleAbort);
      return true;
    }

    function handleAbort() {
      canceled = true;
      if (!settle()) return;
      ws.close();
      reject(new NCALayerError("ncalayer.errors.cancelled", NCALAYER_CANCELLED_CODE));
    }

    signal?.addEventListener("abort", handleAbort, { once: true });

    ws.onopen = () => {};

    ws.onmessage = (event) => {
      if (canceled || settled) return;
      try {
        const data = JSON.parse(event.data);

        if (!handshakeConsumed) {
          handshakeConsumed = true;

          const request = {
            module: "kz.gov.pki.knca.commonUtils",
            method: "createCAdESFromBase64",
            args: ["PKCS12", "SIGNATURE", dataBase64, false],
          };
          ws.send(JSON.stringify(request));
          return;
        }

        settle();
        ws.close();

        if (data.code === "200" && data.responseObject) {
          resolve({ signature: data.responseObject });
        } else if (data.code === NCALAYER_CANCELLED_CODE) {
          reject(new NCALayerError("ncalayer.errors.cancelled", data.code));
        } else {
          reject(new NCALayerError("ncalayer.errors.unknown", data.code));
        }
      } catch {
        if (!settle()) return;
        ws.close();
        reject(new NCALayerError("ncalayer.errors.parseFailed"));
      }
    };

    ws.onerror = () => {
      if (canceled) return;
      if (!settle()) return;
      reject(new NCALayerError("ncalayer.errors.cannotConnect"));
    };

    ws.onclose = (event) => {
      if (canceled) return;
      if (!settle()) return;
      reject(new NCALayerError("ncalayer.errors.connectionClosed", String(event.code)));
    };
  });
}
